import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import { format, getYear } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';

import { setModalOpen } from '../ducks/leaderboardActions';

import TBTooltip from '../../../shared/_TBComponents/Tooltip';
import iconSet from '../../../shared/images/teambuildr-selection.json';

const MAX_VISIBLE_TAGS = 3;

const categoryConfig = {
  L: {
    label: 'LIFT', icon: 'lift', color: '#777', bgColor: '#efefef',
  },
  R: {
    label: 'SAQ+C', icon: 'cardio', color: '#4a9de6', bgColor: '#dbeeff',
  },
  cardio: {
    label: 'SAQ+C', icon: 'cardio', color: '#4a9de6', bgColor: '#dbeeff',
  },
  C: {
    label: 'CIRCUIT', icon: 'circuit', color: '#e8951c', bgColor: '#fff0dc',
  },
};

const genderLabels = {
  0: 'All', 1: 'Male', 2: 'Female', 3: 'Undefined',
};

const rangeTypeLabels = {
  1: 'All Time', 2: 'Last 30 Days', 3: 'Today', 4: 'Custom', 5: 'Current Max',
};

const compareToLabels = {
  0: null, 1: 'All Time', 2: 'Last 30 Days', 3: 'Today', 4: 'Custom', 5: 'Current Max',
};

const formatDateRange = (dates) => {
  if (!dates?.startDate || !dates?.endDate) return 'Custom';
  const start = new Date(dates.startDate);
  const end = new Date(dates.endDate);
  if (getYear(start) === getYear(end)) {
    return `${format(start, 'MMM d')} – ${format(end, 'MMM d, yyyy')}`;
  }
  return `${format(start, 'MMM d, yyyy')} – ${format(end, 'MMM d, yyyy')}`;
};

export const TemplatePadder = styled('div')`
  padding: 8px;
`;

export const Template = styled('div')`
  width: 300px;
  height: 250px;
  background: #ffffff;
  padding: 16px;
  cursor: ${(props) => (props.isLoading ? 'default' : 'pointer')};
  pointer-events: ${(props) => (props.isLoading ? 'none' : 'auto')};
  opacity: ${(props) => (props.isLoading ? 0.6 : 1)};
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  font-family: 'Nunito Sans';
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  position: relative;

  &:hover {
    box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.15);
  }
`;

const ActionButtons = styled('div')`
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  gap: 2px;
`;

const IconButton = styled('button')`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #bbb;
  display: flex;
  align-items: center;
  border-radius: 4px;

  &:hover {
    color: ${(props) => (props.danger ? '#e53935' : '#555')};
    background: #f5f5f5;
  }
`;

export const Header = styled('div')`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 12px;
  padding-right: ${(props) => (props.noButtonPad ? '0' : '52px')};
`;

export const IconBox = styled('div')`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${(props) => props.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const CategoryAndName = styled('div')`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.gap || '2px'};
  flex: 1;
  min-width: 0;
`;

const CategoryLabel = styled('div')`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  color: ${(props) => props.color};
  line-height: 1.2;
`;

const ExerciseName = styled('div')`
  font-size: 16px;
  font-weight: 700;
  color: #222;
  line-height: 1.3;
  height: 42px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const TagsRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 24px;
  margin-bottom: 12px;
`;

const TagsInner = styled('div')`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const Tag = styled('span')`
  display: block;
  background: #f0f0f0;
  color: #555;
  font-size: 11px;
  font-weight: 600;
  padding: 0 8px;
  border-radius: 4px;
  white-space: nowrap;
  height: 22px;
  line-height: 22px;
  flex: 0 1 auto;
  min-width: 30px;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;

  & > div > a {
    display: block;
  }
`;

const PlusTag = styled(Tag)`
  background: #e8e8e8;
  color: #888;
`;

export const InfoRows = styled('div')`
  display: flex;
  flex-direction: column;
`;

const InfoRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 0;
  font-size: 12px;

  & + & {
    border-top: 1px solid #f2f2f2;
  }
`;

const InfoLabel = styled('span')`
  color: #444444;
  font-weight: 400;
  flex: 1;
`;

const InfoValue = styled('span')`
  color: #333;
  font-weight: 600;
`;

const LeaderboardTemplateCard = ({ template, onLoad, readOnly }) => {
  const isLoadingLeaderboard = useSelector((state) => state.leaderboard.ui.isLoadingLeaderboard);
  const dispatch = useDispatch();

  const handleCardClick = (e) => {
    if (e.target.closest('[data-no-click]')) return;
    onLoad(template);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    dispatch(setModalOpen(true, 'Edit Template', template.id));
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    dispatch(setModalOpen(true, 'Delete Template', template.id));
  };

  const config = categoryConfig[template.category] || {
    label: template.category, icon: 'trophy', color: '#888', bgColor: '#f0f0f0',
  };
  const genderLabel = genderLabels[template.gender] ?? 'All';
  const timeFrameLabel = template.rangeType === 4
    ? formatDateRange(template.rangeDates)
    : (rangeTypeLabels[template.rangeType] || '—');
  const compareLabel = template.compareTo === 4
    ? formatDateRange(template.compareDates)
    : (compareToLabels[template.compareTo] || null);

  const visibleGroups = template.groups.slice(0, MAX_VISIBLE_TAGS);
  const hiddenCount = template.groups.length - visibleGroups.length;

  return (
    <TemplatePadder>
      <Template onClick={handleCardClick} isLoading={isLoadingLeaderboard}>
        {!readOnly && (
          <ActionButtons data-no-click>
            <IconButton type='button' onClick={handleEdit} title='Edit template'>
              <IcomoonReact iconSet={iconSet} size={13} icon='pencil' color='currentColor' />
            </IconButton>
            <IconButton type='button' danger onClick={handleDeleteClick} title='Delete template'>
              <IcomoonReact iconSet={iconSet} size={13} icon='trashcan' color='currentColor' />
            </IconButton>
          </ActionButtons>
        )}

        <Header>
          <IconBox bgColor={config.bgColor}>
            <IcomoonReact iconSet={iconSet} size={18} icon={config.icon} color={config.color} />
          </IconBox>
          <CategoryAndName>
            <CategoryLabel color={config.color}>{config.label}</CategoryLabel>
            <ExerciseName title={template.exerciseName}>{template.exerciseName}</ExerciseName>
          </CategoryAndName>
        </Header>

        <TagsRow>
          <TagsInner>
            {visibleGroups.map((g) => (
              <Tag key={g.id}>
                <TBTooltip
                  id={`tag-${template.id}-${g.id}`}
                  displayText={g.name}
                  hoverText={g.name}
                  displayColor='#555'
                  displaySize='11px'
                  displayWeight='600'
                  cursor='default'
                  place='top'
                  tooltipOpacity={1}
                  styledPropsWrapper={{ width: '100%', height: '100%' }}
                  tooltipStyles={{ zIndex: 400 }}
                />
              </Tag>
            ))}
          </TagsInner>
          {hiddenCount > 0 && (
            <PlusTag>
              <TBTooltip
                id={`tag-plus-${template.id}`}
                displayText={`+${hiddenCount}`}
                hoverText={template.groups.slice(MAX_VISIBLE_TAGS).map((g) => g.name).join(', ')}
                displayColor='#888'
                displaySize='11px'
                displayWeight='600'
                cursor='default'
                place='top'
                tooltipOpacity={1}
                styledPropsWrapper={{ width: '100%', height: '100%' }}
                tooltipStyles={{
                  whiteSpace: 'normal',
                  maxWidth: '240px',
                  wordBreak: 'break-word',
                  zIndex: 400,
                }}
              />
            </PlusTag>
          )}
        </TagsRow>

        <InfoRows>
          <InfoRow>
            <IcomoonReact iconSet={iconSet} size={12} icon='group' color='#444444' />
            <InfoLabel>Included Athletes</InfoLabel>
            <InfoValue>{template.mustBeInAllGroups ? 'All Groups' : 'Any Group'}</InfoValue>
          </InfoRow>
          <InfoRow>
            <IcomoonReact iconSet={iconSet} size={12} icon='user' color='#444444' />
            <InfoLabel>Gender</InfoLabel>
            <InfoValue>{genderLabel}</InfoValue>
          </InfoRow>
          <InfoRow>
            <IcomoonReact iconSet={iconSet} size={12} icon='calendar' color='#444444' />
            <InfoLabel>Time Frame</InfoLabel>
            <InfoValue>{timeFrameLabel}</InfoValue>
          </InfoRow>
          {compareLabel && (
            <InfoRow>
              <IcomoonReact iconSet={iconSet} size={12} icon='percent' color='#444444' />
              <InfoLabel>Compare To</InfoLabel>
              <InfoValue>{compareLabel}</InfoValue>
            </InfoRow>
          )}
        </InfoRows>
      </Template>
    </TemplatePadder>
  );
};

LeaderboardTemplateCard.defaultProps = {
  readOnly: false,
};

LeaderboardTemplateCard.propTypes = {
  template: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    category: PropTypes.string.isRequired,
    exerciseName: PropTypes.string.isRequired,
    groups: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    })).isRequired,
    mustBeInAllGroups: PropTypes.bool,
    gender: PropTypes.number.isRequired,
    rangeType: PropTypes.number.isRequired,
    rangeDates: PropTypes.shape({
      startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    }),
    compareTo: PropTypes.number,
    compareDates: PropTypes.shape({
      startDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    }),
  }).isRequired,
  onLoad: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

export default LeaderboardTemplateCard;
